import { library } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faUnlink } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { ReactElement } from 'react';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { LazyImage } from '../../../../__legacy__/components/LazyImage';
import { doNothingAsync } from '../../../../__legacy__/helpers/function.helpers';
import { ImageSetBadge } from '../../../../__legacy__/modules/annotation/components/ImageSetBadge';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { S_ClampLines } from '../../../../design/misc/S_ClampLines';
import { IPredictionImage } from '../../jobPredictions.model';
import { TRAINING_VARIANT } from '../../jobs.model';
import { AnomalyDetection } from './AnomalyDetection';
import { LocateObjects } from './LocateObjects';
import { LocatePoints } from './LocatePoints';
import { ObjectClassification } from './ObjectClassification';

library.add(faUnlink, faEdit);

interface ThumbnailProps {
  onClickLink?: string;
  image: IPredictionImage;
  authToken: string;
  showPredictions: boolean;
  threshold: number | number[];
  variant: TRAINING_VARIANT | null;
}

export const PredictionImageThumbnail = ({ image, authToken, showPredictions, threshold, onClickLink, variant }: ThumbnailProps): ReactElement => {
  const thumbClass = (): string => {
    if (!showPredictions) {
      return '';
    }

    switch (variant) {
      case TRAINING_VARIANT.ANOMALY_DETECTION_4:
      case TRAINING_VARIANT.ANOMALY_DETECTION_7:
      case TRAINING_VARIANT.ANOMALY_DETECTION_8: {
        const score = image.prediction.score;

        if (score === undefined || typeof threshold === 'number' || score === null) {
          return 'not-submitted';
        }

        if (score < threshold[0]) {
          return 'approved';
        }
        if (score > threshold[1]) {
          return 'rejected';
        }
        if (score >= threshold[0] && score <= threshold[1]) {
          return 'warning';
        }

        return 'not-submitted';
      }
      case TRAINING_VARIANT.OBJECT_CLASSIFICATION:
        if (image.prediction.groundTruth && image.prediction.prediction) {
          return image.prediction.groundTruth.className === image.prediction.prediction ? 'approved' : 'rejected';
        }
        return 'not-submitted';
      case null:
      case TRAINING_VARIANT.FEATURE_DETECTION:
      case TRAINING_VARIANT.LOCATE_OBJECTS:
      case TRAINING_VARIANT.LOCATE_POINTS:
      default:
        return 'not-submitted';
    }
  };

  const renderPredictions = (): ReactElement | null => {
    // TODO: Unify those components with predictionInspector ones?
    // Just make marking optional
    switch (variant) {
      case TRAINING_VARIANT.ANOMALY_DETECTION_4:
      case TRAINING_VARIANT.ANOMALY_DETECTION_7:
      case TRAINING_VARIANT.ANOMALY_DETECTION_8:
        return (
          <AnomalyDetection
            score={image.prediction.score}
            groundTruth={image.prediction.groundTruth?.className}
          />
        );
      case TRAINING_VARIANT.OBJECT_CLASSIFICATION:
        return (
          <ObjectClassification
            prediction={image.prediction.prediction}
            groundTruth={image.prediction.groundTruth?.className}
            confidence={image.prediction.confidence}
          />
        );
      case TRAINING_VARIANT.LOCATE_POINTS:
        return (
          <LocatePoints
            groundTruth={image.prediction.groundTruth}
            predictedPoints={image.predictedPoints}
            threshold={threshold as number}
          />
        );
      case TRAINING_VARIANT.LOCATE_OBJECTS:
        return (
          <LocateObjects
            groundTruth={image.prediction.groundTruth}
            predictedRectangles={image.predictedRectangles}
            threshold={threshold as number}
          />
        );
      case null:
      case TRAINING_VARIANT.FEATURE_DETECTION:
      default:
        return null;
    }
  };

  const thumbnail = (
    <LazyLoadComponent threshold={300}>
      <LazyImage
        source={image.url}
        headers={new Headers({ Authorization: `Bearer ${authToken}` })}
      />
      {image.maskUrl !== null && showPredictions && (
        <div className='image-mask'>
          <LazyImage
            source={image.maskUrl}
            headers={new Headers({ Authorization: `Bearer ${authToken}` })}
          />
        </div>
      )}
    </LazyLoadComponent>
  );

  return (
    <div className={`thumb ${thumbClass()}`}>
      <div className={classNames('image-container', { disabled: !onClickLink })}>
        <div className='image-wrapper'>
          {onClickLink !== undefined ? (
            <S_Link2
              color='text'
              to={onClickLink}
            >
              {thumbnail}
            </S_Link2>
          ) : (
            thumbnail
          )}
        </div>
        {!!image.imageSetType && (
          <ImageSetBadge
            set={image.imageSetType}
            disabled={true}
            onChange={doNothingAsync}
          />
        )}
      </div>
      <div className='description'>
        <div className='file-name'>
          <S_ClampLines
            text={image.name}
            lines={2}
          />
        </div>
        {showPredictions && renderPredictions()}
      </div>
    </div>
  );
};
